import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type TechnicianOnboardingStatus =
  | 'draft' | 'submitted' | 'verified' | 'approved' | 'active' | 'rejected' | 'sent_back' | 'clarification_required';
export type TechnicianReviewDecision = 'verify' | 'approve' | 'reject' | 'send_back' | 'request_clarification';

export const TECH_TYPES = ['internal', 'contract', 'freelance', 'vendor'] as const;
export const GENDERS = ['male', 'female', 'other'] as const;
export const SKILL_CATEGORIES = ['electrical_installation', 'ev_charger_installation', 'evcpd_installation', 'civil_work', 'earthing', 'networking', 'commissioning', 'service', 'om', 'amc', 'survey', 'fabrication'] as const;
export const EXPERIENCE_LEVELS = ['fresher', 'junior', 'mid_level', 'senior', 'expert'] as const;
export const SERVICE_COVERAGES = ['pan_india', 'state_level', 'district_level'] as const;
export const EMPLOYMENT_TYPES = ['full_time', 'contract', 'freelancer', 'third_party'] as const;
export const PAYMENT_TYPES = ['per_site', 'monthly', 'daily', 'fixed_salary'] as const;
export const OPERATIONAL_STATUSES = ['active', 'inactive', 'on_leave', 'suspended', 'resigned', 'blacklisted', 'deployed'] as const;
export const TECH_DOC_TYPES = ['aadhaar', 'pan', 'photograph', 'bank', 'driving_license', 'experience_certificate', 'skill_certificate', 'police_verification'] as const;

export interface TechDocument { id?: number; document_type: string; file_name?: string | null; file_url?: string | null }

export interface TechnicianApplication {
  id: number;
  application_code: string;
  technician_id: number | null;
  login_user_id: number | null;
  status: TechnicianOnboardingStatus;
  current_stage: string;
  technician_name: string;
  technician_type: string | null;
  gender: string | null;
  date_of_birth: string | null;
  profile_photo_url: string | null;
  mobile: string | null;
  alternate_mobile: string | null;
  personal_email: string | null;
  emergency_contact_person: string | null;
  emergency_contact_number: string | null;
  emergency_relationship: string | null;
  aadhaar: string | null;
  pan_number: string | null;
  driving_license_number: string | null;
  state_employee_id: string | null;
  passport_number: string | null;
  current_address_line1: string | null;
  current_address_line2: string | null;
  current_state_id: number | null;
  current_district: string | null;
  current_city: string | null;
  current_pincode: string | null;
  permanent_same_as_current: boolean;
  permanent_address_line1: string | null;
  permanent_state_id: number | null;
  permanent_district: string | null;
  permanent_city: string | null;
  permanent_pincode: string | null;
  skill_categories: string[];
  experience_years: number | null;
  experience_level: string | null;
  certification_available: boolean;
  certification_details: string | null;
  strong_states: string[];
  service_coverage: string | null;
  available_for_travel: boolean;
  own_vehicle: boolean;
  vehicle_type: string | null;
  joining_date: string | null;
  employment_type: string | null;
  reporting_manager_id: number | null;
  assigned_vendor_id: number | null;
  assigned_region_id: number | null;
  operational_status: string | null;
  payment_type: string | null;
  daily_rate: number | null;
  per_site_rate: number | null;
  monthly_salary: number | null;
  bank_name: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  upi_id: string | null;
  create_login: boolean;
  sites_assigned: number;
  sites_completed: number;
  average_tat: number | null;
  sla_achievement_pct: number | null;
  customer_rating: number | null;
  quality_score: number | null;
  rework_count: number;
  payment_pending: number;
  source_type: string | null;
  submitted_at: string | null;
  completed_steps: number[];
  documents: TechDocument[];
  verification_status?: string | null;
  approval_status?: string | null;
  workflow?: Array<{ id: number; stage: string; stage_order: number; status: string; assigned_role: string | null; remarks: string | null }>;
}

export const technicianOnboardingApi = {
  async get(id: number): Promise<TechnicianApplication> {
    return (await apiClient.get<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}`)).data.data;
  },
  async create(payload: Record<string, unknown>): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>('/technician-onboarding', payload)).data.data;
  },
  async saveDraft(id: number, payload: Record<string, unknown>): Promise<TechnicianApplication> {
    return (await apiClient.put<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/draft`, payload)).data.data;
  },
  async submit(id: number): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/submit`, {})).data.data;
  },
  async respondClarification(id: number): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/respond-clarification`, {})).data.data;
  },
  async list(params: Record<string, unknown> = {}): Promise<{ data: TechnicianApplication[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<TechnicianApplication[]>>('/technician-onboarding', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async review(id: number, decision: TechnicianReviewDecision, remarks?: string): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/review`, { decision, remarks })).data.data;
  },
  async withdraw(id: number): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/withdraw`, {})).data.data;
  },
  async remove(id: number): Promise<{ deleted: true }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true }>>(`/technician-onboarding/${id}`)).data.data;
  },
  async clone(id: number): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/clone`, {})).data.data;
  },
  async reopen(id: number): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/reopen`, {})).data.data;
  },
  async changeStatus(id: number, status: string): Promise<TechnicianApplication> {
    return (await apiClient.post<ApiSuccess<TechnicianApplication>>(`/technician-onboarding/${id}/status`, { status })).data.data;
  },
};

export interface TechnicianOnboardingDashboard {
  kpis: Record<string, number>;
  charts: Record<string, Array<{ label: string; value: number }>>;
  tables: { recentlySubmitted: Array<Record<string, unknown>>; topPerformers: Array<Record<string, unknown>> };
}

export const technicianDashboardApi = {
  async dashboard(): Promise<TechnicianOnboardingDashboard> {
    return (await apiClient.get<ApiSuccess<TechnicianOnboardingDashboard>>('/technician-onboarding-dashboard')).data.data;
  },
};
